import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";

export const formatCurrency = (amount) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
};

export const formatNumber = (num) => {
  return new Intl.NumberFormat("en-IN").format(num || 0);
};

export const formatCompactCurrency = (amount) => {
  if (amount === undefined || amount === null) return "₹0";
  const abs = Math.abs(amount);
  if (abs >= 1000000000) return `₹${(amount / 1000000000).toFixed(1)}B`;
  if (abs >= 1000000) return `₹${(amount / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `₹${(amount / 1000).toFixed(1)}k`;
  return `₹${amount}`;
};

export const formatCompactNumber = (num) => {
  if (num === undefined || num === null) return "0";
  const abs = Math.abs(num);
  if (abs >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return String(num);
};

export default function DashboardBarChart({
  data = [],
  categoryKey = "name",
  valueKey = "value",
  valueLabel = "Value",
  isCurrency = false,
  isDual = false,
  secondaryKey = "overdue_tasks",
  secondaryLabel = "Overdue Tasks",
  emptyMessage = "No data available to display in chart.",
  scaleMode = "linear", // "linear" | "log"
  chartOrientation = "vertical", // "vertical" (columns) | "horizontal" (horizontal bars)
  showValuesOnBars = true,
}) {
  const isLog = scaleMode === "log";
  const isHorizontal = chartOrientation === "horizontal";

  // Calculate total for percentage context in tooltips
  const totalValue = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
  }, [data, valueKey]);

  // Transformed data for safe log scaling without SVG/NaN errors
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((item) => {
      const rawVal = Number(item[valueKey]) || 0;
      const rawSec = Number(item[secondaryKey]) || 0;

      const displayVal = isLog
        ? rawVal > 0
          ? Math.log10(rawVal)
          : 0
        : rawVal;

      const displaySec = isLog
        ? rawSec > 0
          ? Math.log10(rawSec)
          : 0
        : rawSec;

      return {
        ...item,
        _displayVal: displayVal,
        _displaySec: displaySec,
        _rawVal: rawVal,
        _rawSec: rawSec,
      };
    });
  }, [data, valueKey, secondaryKey, isLog]);

  // Calculate ticks and domain when log scale is active
  const { maxLogDomain, logTicks } = useMemo(() => {
    if (!isLog || !data || data.length === 0) {
      return { maxLogDomain: 1, logTicks: [] };
    }
    const maxRaw = Math.max(
      ...data.map((d) =>
        Math.max(
          Number(d[valueKey]) || 0,
          isDual ? Number(d[secondaryKey]) || 0 : 0
        )
      ),
      10
    );
    const domainMax = Math.max(1, Math.ceil(Math.log10(maxRaw)));
    const ticks = [];
    const step = domainMax > 8 ? 2 : 1;
    for (let i = 0; i <= domainMax; i += step) {
      ticks.push(i);
    }
    if (ticks[ticks.length - 1] < domainMax) {
      ticks.push(domainMax);
    }
    return { maxLogDomain: domainMax, logTicks: ticks };
  }, [isLog, data, valueKey, secondaryKey, isDual]);

  if (!data || data.length === 0) {
    return (
      <div className="chart-empty-state" data-testid="chart-empty-state">
        <p className="chart-empty-text">{emptyMessage}</p>
      </div>
    );
  }

  // Value axis tick formatter
  const valueAxisFormatter = (val) => {
    if (isLog) {
      if (val === 0) return isCurrency ? "₹0" : "0";
      const realVal = Math.pow(10, val);
      return isCurrency
        ? formatCompactCurrency(realVal)
        : formatCompactNumber(realVal);
    }
    if (isCurrency) {
      return formatCompactCurrency(val);
    }
    return formatCompactNumber(val);
  };

  // Truncate long category labels cleanly
  const truncateLabel = (label) => {
    if (!label) return "";
    const str = String(label);
    const maxLen = isHorizontal ? 18 : 14;
    return str.length > maxLen ? `${str.substring(0, maxLen - 2)}…` : str;
  };

  // Bar labels
  const barLabelFormatter = (val, entry) => {
    if (!showValuesOnBars) return "";
    const raw = entry && entry._rawVal !== undefined ? entry._rawVal : val;
    if (!raw || raw === 0) return "";
    return isCurrency ? formatCompactCurrency(raw) : formatCompactNumber(raw);
  };

  const secondaryBarLabelFormatter = (val, entry) => {
    if (!showValuesOnBars) return "";
    const raw = entry && entry._rawSec !== undefined ? entry._rawSec : val;
    if (!raw || raw === 0) return "";
    return isCurrency ? formatCompactCurrency(raw) : formatCompactNumber(raw);
  };

  // Custom sleek tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const item = payload[0].payload;
    const rawVal =
      item._rawVal !== undefined ? item._rawVal : item[valueKey] || 0;
    const pct =
      totalValue > 0 ? ((rawVal / totalValue) * 100).toFixed(1) : "0.0";

    return (
      <div className="chart-custom-tooltip">
        <div className="chart-tooltip-header">
          <span className="chart-tooltip-title">
            {label || item[categoryKey]}
          </span>
        </div>
        <div className="chart-tooltip-body">
          <div className="chart-tooltip-row">
            <span
              className="chart-tooltip-indicator"
              style={{ backgroundColor: "#2563eb" }}
            />
            <span className="chart-tooltip-label">{valueLabel}:</span>
            <span className="chart-tooltip-value">
              {isCurrency ? formatCurrency(rawVal) : formatNumber(rawVal)}
            </span>
          </div>

          {totalValue > 0 && !isDual && (
            <div className="chart-tooltip-subtext">
              Share: <strong>{pct}%</strong> of total
            </div>
          )}

          {isDual && item._rawSec !== undefined && (
            <div className="chart-tooltip-row">
              <span
                className="chart-tooltip-indicator"
                style={{ backgroundColor: "#ef4444" }}
              />
              <span className="chart-tooltip-label">{secondaryLabel}:</span>
              <span className="chart-tooltip-value">
                {isCurrency
                  ? formatCurrency(item._rawSec)
                  : formatNumber(item._rawSec)}
              </span>
            </div>
          )}
        </div>
        {isLog && (
          <div className="chart-tooltip-footnote">
            Scale: Balanced View (easier comparison)
          </div>
        )}
      </div>
    );
  };

  // Dynamic height for horizontal charts with many items
  const chartHeight = isHorizontal
    ? Math.max(340, Math.min(600, chartData.length * 36 + 60))
    : 380;

  // Key to use for plotting bars
  const activeValueKey = isLog ? "_displayVal" : valueKey;
  const activeSecKey = isLog ? "_displaySec" : secondaryKey;

  return (
    <div
      className="dashboard-barchart-container"
      data-testid="dashboard-bar-chart"
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        {isHorizontal ? (
          /* Horizontal Bar Layout (Rows) */
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 15, right: 35, left: 10, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke="#e2e8f0"
            />
            <XAxis
              type="number"
              domain={isLog ? [0, maxLogDomain] : [0, "auto"]}
              ticks={isLog ? logTicks : undefined}
              tickFormatter={valueAxisFormatter}
              tick={{ fontSize: 12, fill: "#475569" }}
            />
            <YAxis
              type="category"
              dataKey={categoryKey}
              tickFormatter={truncateLabel}
              tick={{ fontSize: 12, fill: "#475569" }}
              width={120}
              interval={0}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(37, 99, 235, 0.06)", radius: 4 }}
            />
            {isDual ? (
              <>
                <Legend wrapperStyle={{ paddingTop: 10 }} />
                <Bar
                  dataKey={activeValueKey}
                  name={valueLabel}
                  fill="#3b82f6"
                  radius={[0, 4, 4, 0]}
                  minPointSize={8}
                  maxBarSize={28}
                >
                  {showValuesOnBars && (
                    <LabelList
                      dataKey={activeValueKey}
                      position="right"
                      formatter={barLabelFormatter}
                      style={{ fontSize: 11, fontWeight: 600, fill: "#475569" }}
                    />
                  )}
                </Bar>
                <Bar
                  dataKey={activeSecKey}
                  name={secondaryLabel}
                  fill="#ef4444"
                  radius={[0, 4, 4, 0]}
                  minPointSize={8}
                  maxBarSize={28}
                >
                  {showValuesOnBars && (
                    <LabelList
                      dataKey={activeSecKey}
                      position="right"
                      formatter={secondaryBarLabelFormatter}
                      style={{ fontSize: 11, fontWeight: 600, fill: "#ef4444" }}
                    />
                  )}
                </Bar>
              </>
            ) : (
              <Bar
                dataKey={activeValueKey}
                name={valueLabel}
                fill="#2563eb"
                radius={[0, 4, 4, 0]}
                minPointSize={8}
                maxBarSize={32}
              >
                {showValuesOnBars && (
                  <LabelList
                    dataKey={activeValueKey}
                    position="right"
                    formatter={barLabelFormatter}
                    style={{ fontSize: 11, fontWeight: 600, fill: "#334155" }}
                  />
                )}
              </Bar>
            )}
          </BarChart>
        ) : (
          /* Vertical Column Layout (Columns) */
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 25, left: 15, bottom: chartData.length > 5 ? 55 : 35 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />
            <XAxis
              dataKey={categoryKey}
              tickFormatter={truncateLabel}
              interval={chartData.length > 10 ? "preserveStartEnd" : 0}
              angle={chartData.length > 5 ? -25 : 0}
              textAnchor={chartData.length > 5 ? "end" : "middle"}
              tick={{ fontSize: 12, fill: "#475569" }}
              height={chartData.length > 5 ? 60 : 35}
            />
            <YAxis
              domain={isLog ? [0, maxLogDomain] : [0, "auto"]}
              ticks={isLog ? logTicks : undefined}
              tickFormatter={valueAxisFormatter}
              tick={{ fontSize: 12, fill: "#475569" }}
              width={70}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(37, 99, 235, 0.06)", radius: 4 }}
            />
            {isDual ? (
              <>
                <Legend wrapperStyle={{ paddingTop: 10 }} />
                <Bar
                  dataKey={activeValueKey}
                  name={valueLabel}
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                  minPointSize={8}
                  maxBarSize={45}
                >
                  {showValuesOnBars && chartData.length <= 15 && (
                    <LabelList
                      dataKey={activeValueKey}
                      position="top"
                      formatter={barLabelFormatter}
                      style={{ fontSize: 11, fontWeight: 600, fill: "#3b82f6" }}
                    />
                  )}
                </Bar>
                <Bar
                  dataKey={activeSecKey}
                  name={secondaryLabel}
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                  minPointSize={8}
                  maxBarSize={45}
                >
                  {showValuesOnBars && chartData.length <= 15 && (
                    <LabelList
                      dataKey={activeSecKey}
                      position="top"
                      formatter={secondaryBarLabelFormatter}
                      style={{ fontSize: 11, fontWeight: 600, fill: "#ef4444" }}
                    />
                  )}
                </Bar>
              </>
            ) : (
              <Bar
                dataKey={activeValueKey}
                name={valueLabel}
                fill="#2563eb"
                radius={[4, 4, 0, 0]}
                minPointSize={8}
                maxBarSize={55}
              >
                {showValuesOnBars && chartData.length <= 15 && (
                  <LabelList
                    dataKey={activeValueKey}
                    position="top"
                    formatter={barLabelFormatter}
                    style={{ fontSize: 11, fontWeight: 600, fill: "#334155" }}
                  />
                )}
              </Bar>
            )}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
